import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';

/**
 * Image upload helpers for Firebase Storage.
 *
 * Callers: user avatars (`user-avatars/{uid}/...`), company logos
 * (`company-assets/{cid}/...`), and support screenshots
 * (`support-attachments/{uid}/{reportId}/...`). Path prefixes match the
 * scopes already defined in `storage.rules` so we don't have to grow
 * a parallel rule set. Both flows are FE-driven uploads with the URL
 * persisted via the canonical REST API (`PUT /users/me`,
 * `PUT /companies/:id`) — the BE never touches the bytes.
 *
 * Validation lives here (size + MIME) so we fail fast before paying the
 * upload round-trip.
 */

export const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export class ImageUploadError extends Error {
  constructor(
    message: string,
    public readonly code: 'too_large' | 'invalid_type' | 'upload_failed',
  ) {
    super(message);
    this.name = 'ImageUploadError';
  }
}

function validate(file: File): void {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new ImageUploadError(
      `Unsupported image type: ${file.type}. Use PNG, JPEG or WebP.`,
      'invalid_type',
    );
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    throw new ImageUploadError(
      `Image is too large (${Math.round(file.size / 1024)} KB). Max ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)} MB.`,
      'too_large',
    );
  }
}

/**
 * Uploads a file to Firebase Storage and returns the public download URL.
 * Filename is namespaced by `prefix/{ownerId}/{timestamp}-{originalName}`
 * so re-uploads don't collide and old files stay around for cache busts.
 *
 * Caller is responsible for persisting the returned URL on the relevant
 * Firestore document via the canonical REST endpoint.
 */
export type ImageUploadPrefix = 'user-avatars' | 'company-assets' | 'support-attachments';

export async function uploadImage(
  file: File,
  options:
    | { prefix: 'user-avatars' | 'company-assets'; ownerId: string }
    | { prefix: 'support-attachments'; ownerId: string; reportId: string },
): Promise<string> {
  validate(file);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path =
    options.prefix === 'support-attachments'
      ? `${options.prefix}/${options.ownerId}/${options.reportId}/${Date.now()}-${safeName}`
      : `${options.prefix}/${options.ownerId}/${Date.now()}-${safeName}`;
  try {
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file, { contentType: file.type });
    return await getDownloadURL(ref);
  } catch (err) {
    // Surface the underlying Firebase Storage error code/message so the
    // caller can show a useful toast. Common codes:
    //   storage/unauthorized — Storage rule denied the write
    //   storage/quota-exceeded — bucket quota hit
    //   storage/canceled — user aborted (rare)
    // Logging via console.error so devs can grep DevTools when a user
    // reports "upload doesn't work" without the toast text.
    const fbErr = err as { code?: string; message?: string } | undefined;
    const detail = fbErr?.code ? `${fbErr.code} — ${fbErr.message ?? ''}` : (fbErr?.message ?? 'Upload failed');
    console.error('[uploadImage] Firebase Storage upload failed:', detail, err);
    throw new ImageUploadError(detail, 'upload_failed');
  }
}
